import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Box, Paper, Typography, TextField, Select, MenuItem, FormControl, InputLabel, Grid, Button, IconButton, InputAdornment, Card, CardContent } from '@mui/material';
import { Clear as ClearIcon, Search as SearchIcon, PictureAsPdf as PdfIcon } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import RequestItem from './RequestItem';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const AdminDashboard = () => {
  const [licenceRequests, setLicenceRequests] = useState([]);
  const [availableLicenses, setLicenses] = useState([]);

  const [filterStatus, setFilterStatus] = useState('All');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');

  const [isLoading, setIsLoading] = useState(true);

  const user = useSelector((state) => state.user);

  useEffect(() => {
    const fetchLicenceRequests = async () => {
      try {
        const response = await axios.post(
          `${apiBaseUrl}/get-all-licence-requests`,
          user,
          {
            headers: {
              Authorization: `Bearer ${user.token}`
            }
          }
        ); // Update with your API endpoint
        if (response.data?.requests) {
          setLicenceRequests(response.data.requests);
          setLicenses(response.data.licenses)
          setIsLoading(false);
        }

      } catch (error) {
        console.error('Failed to fetch licence requests:', error);
        // Handle error (e.g., show error message)
        setIsLoading(true);
      }
    };

    fetchLicenceRequests();
  }, []);

  const handleRequestUpdate = async (updatedRequest) => {
    try {

      const response = await axios.post(
        `${apiBaseUrl}/handleLicenceRequest`,
        updatedRequest,
        {
          headers: {
            Authorization: `Bearer ${user.token}`
          }
        }
      ); // Update with your API endpoint
      if (response.data?.requests) {
        setLicenceRequests(response.data.requests);
      }
      // Handle success response
    } catch (error) {
      console.error('There was a problem with the fetch operation:', error);
    }
  };

  const filteredRequests = licenceRequests.filter(request => {
    const matchesStatus = filterStatus === 'All' || request.status === filterStatus.toLowerCase();
    const matchesCustomer = request.company_name.toLowerCase().includes(filterCustomer.toLowerCase());

    let matchesDate = true;
    if (filterStartDate) {
      matchesDate = matchesDate && new Date(request.start_date) >= new Date(filterStartDate);
    }
    if (filterEndDate) {
      matchesDate = matchesDate && (!request.end_date || new Date(request.end_date) <= new Date(filterEndDate));
    }

    return matchesStatus && matchesCustomer && matchesDate;
  });

  const clearFilters = () => {
    setFilterStatus('All');
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterCustomer('');
  };

  const generatePDF = () => {
    const doc = new jsPDF();

    const sortedRequests = [...filteredRequests].sort((a, b) => {
      const customerA = a.company_name.toLowerCase();
      const customerB = b.company_name.toLowerCase();
      if (customerA < customerB) return -1;
      if (customerA > customerB) return 1;
      return new Date(a.start_date) - new Date(b.start_date);
    });

    const tableColumn = ["Status", "Customer", "Production", "Start Date", "End Date", "Licences"];
    const tableRows = [];

    sortedRequests.forEach(request => {
      const licensesString = request.licenses && request.licenses.length > 0
        ? request.licenses.map(l => `${l.name}: ${l.value || 'N/A'}`).join('\n')
        : `Count: ${request.licences_count}`;

      const requestData = [
        request.status,
        request.company_name,
        request.production_name,
        new Date(request.start_date).toLocaleDateString(),
        request.end_date ? new Date(request.end_date).toLocaleDateString() : 'N/A',
        licensesString
      ];
      tableRows.push(requestData);
    });

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
    });

    doc.text("Licence Requests", 14, 15);
    doc.save("licence_requests.pdf");
  };

  return (
    <Paper sx={{ minHeight: '100vh', backgroundColor: "#f4f6f8", margin: 0, padding: 0 }}>

      <Box sx={{ padding: 2 }}>
        <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#333', marginBottom: 2 }}>
          Admin Dashboard
        </Typography>

        <Card sx={{ marginBottom: 2, borderRadius: 2, boxShadow: 3 }}>
          <CardContent sx={{ padding: 2, "&:last-child": { paddingBottom: 2 } }}>
            <Grid container spacing={2} justifyContent="center" alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filterStatus}
                    label="Status"
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Approved">Approved</MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Customer"
                  variant="outlined"
                  size="small"
                  value={filterCustomer}
                  onChange={(e) => setFilterCustomer(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: filterCustomer && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setFilterCustomer('')} edge="end" size="small">
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  InputProps={{
                    endAdornment: filterStartDate && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setFilterStartDate('')} edge="end" size="small">
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  InputProps={{
                    endAdornment: filterEndDate && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setFilterEndDate('')} edge="end" size="small">
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2} display="flex" justifyContent="center">
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={clearFilters}
                  startIcon={<ClearIcon />}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  Reset
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={generatePDF}
                  startIcon={<PdfIcon />}
                  size="small"
                >
                  PDF
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
          {filteredRequests.map((request, index) => (
            <RequestItem
              key={index}
              request={request}
              availableLicenses={availableLicenses}
              onRequestUpdate={handleRequestUpdate}
            />
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default AdminDashboard;
